import { isFeatureEnabled } from '@wix/answers-experiments';
import { PRESENCE_EVENT, TICKET_UPDATED_EVENT } from '../..';
export var DUPLEXER_IDLE_WS_STATUS = 3000;
export var PubSubEventType;
(function (PubSubEventType) {
    PubSubEventType["SUBSCRIBE"] = "subscribe";
    PubSubEventType["SUBSCRIBE_SUCCESS"] = "subscribe-success";
    PubSubEventType["TIMED_OUT"] = "timed-out";
    PubSubEventType["PUBLISH"] = "publish";
    PubSubEventType["SEND"] = "send";
    PubSubEventType["CONNECT_TO_DUPLEXER"] = "connect-to-duplexer";
    PubSubEventType["DISCONNECT_FROM_DUPLEXER"] = "disconnect-from-duplexer";
    PubSubEventType["SUBSCRIBE_CHANNEL"] = "subscribe-channel";
    PubSubEventType["SUBSCRIBE_CHANNEL_SUCCESS"] = "subscribe-channel-success";
    PubSubEventType["SUBSCRIBE_CHANNEL_FAIL"] = "subscribe-channel-fail";
    PubSubEventType["UNSUBSCRIBE_CHANNEL"] = "unsubscribe-channel";
    PubSubEventType["DUPLEXER_CONNECTED"] = "duplexer-connected";
    PubSubEventType["DUPLEXER_DISCONNECTED"] = "duplexer-disconnected";
    PubSubEventType["DUPLEXER_CONNECT_ERROR"] = "duplexer-connect-error";
    PubSubEventType["RECONNECT_TO_DUPLEXER"] = "reconnect-to-duplexer";
    PubSubEventType["PUBLISH_RUNTIME_ERROR"] = "publish-runtime-error";
    PubSubEventType["SUBSCRIBE_RUNTIME_ERROR"] = "subscribe-runtime-error";
    PubSubEventType["UNSUBSCRIBE_RUNTIME_ERROR"] = "unsubscribe-runtime-error";
    PubSubEventType["FALLBACK_TO_POLLING"] = "fallback-to-polling";
    PubSubEventType["END_FALLBACK"] = "end-fallback";
    PubSubEventType["MESSAGE_RECEIVED"] = "message-received";
})(PubSubEventType || (PubSubEventType = {}));
var getPrefixedChannelName = function (channelName) { return "@private-".concat(channelName); };
var getPrefixedEventName = function (name) { return "client-".concat(name); };
export var createDuplexerPubSub = function (duplexer, apiKey, hostUrl, logger, shouldConnectPerSubscription) {
    var connections = {};
    var connectionStatuses = {};
    var channels = {};
    // so we can unsubscribe from channel when there are no more handlers
    var channelHandlerCounts = {};
    var getConnectionName = function (channelName) { return shouldConnectPerSubscription ? channelName : 'global'; };
    return {
        subscribe: function (channelName, eventName, callback, authPath, onError, isClient, authPayload) {
            if (authPayload === void 0) { authPayload = {}; }
            var handlerAdded = false;
            var timer;
            var reconnectTimer;
            logger(PubSubEventType.SUBSCRIBE, { channelName: channelName, eventName: eventName });
            return new Promise(function (resolve) {
                var onRecover;
                timer = setTimeout(function () {
                    if (!handlerAdded) {
                        logger(PubSubEventType.TIMED_OUT, { channelName: channelName, eventName: eventName });
                        onRecover = onError();
                        resolve(unsubscribe);
                    }
                }, 10000);
                var unsubscribe = function () {
                    if (onRecover && typeof onRecover === 'function') {
                        logger(PubSubEventType.END_FALLBACK, { channelName: channelName, eventName: eventName, context: 'unsubscribe' });
                        onRecover();
                        onRecover = undefined;
                    }
                    else {
                        unsubscribeFromDuplexer();
                    }
                };
                var connectToDuplexer = function () {
                    logger(PubSubEventType.CONNECT_TO_DUPLEXER, { channelName: channelName, eventName: eventName });
                    connectionStatuses[getConnectionName(channelName)] = 'connecting';
                    connections[getConnectionName(channelName)] = duplexer.connect({
                        apiKey: apiKey,
                        auth: {
                            url: "".concat(hostUrl, "/").concat(authPath),
                            onBeforeRequest: function () { return authPayload; },
                        },
                    });
                    addConnectionEventHandlers();
                };
                var addConnectionEventHandlers = function () {
                    connections[getConnectionName(channelName)].on('@duplexer:connected', function () {
                        connectionStatuses[getConnectionName(channelName)] = 'connected';
                        logger(PubSubEventType.DUPLEXER_CONNECTED, { channelName: channelName, eventName: eventName });
                        if (!channels[channelName]) {
                            connectToChannel();
                        }
                        else if (channels[channelName].status === 'connecting') {
                            addSubscriptionHandlers();
                        }
                        else if (!handlerAdded) {
                            addSubscriptionHandlers();
                            addEventHandlerToChannel();
                        }
                    });
                    connections[getConnectionName(channelName)].on('@duplexer:connect_error', function (error) {
                        logger(PubSubEventType.DUPLEXER_CONNECT_ERROR, { channelName: channelName, eventName: eventName, error: error });
                        if (!onRecover) {
                            logger(PubSubEventType.FALLBACK_TO_POLLING, { channelName: channelName, eventName: eventName, error: error, context: 'connect error' });
                            onRecover = onError();
                            // this is resolved, not rejected, because fallback is handled at this level.
                            // if some flow leads to the promise resolving multiple times that is not a problem: https://stackoverflow.com/a/18218542/10550644
                            resolve(unsubscribe);
                        }
                    });
                    connections[getConnectionName(channelName)].on('@duplexer:disconnected', function (error) {
                        var disconnectTime = Date.now();
                        logger(PubSubEventType.DUPLEXER_DISCONNECTED, { channelName: channelName, eventName: eventName, error: error });
                        if (error && !onRecover) {
                            logger(PubSubEventType.FALLBACK_TO_POLLING, { channelName: channelName, eventName: eventName, error: error, context: 'duplexer disconnected' });
                            onRecover = onError();
                        }
                        if ((error === null || error === void 0 ? void 0 : error.status) === DUPLEXER_IDLE_WS_STATUS) {
                            var reconnect_1 = function () {
                                logger(PubSubEventType.RECONNECT_TO_DUPLEXER, { channelName: channelName, eventName: eventName, error: error, timeDisconnected: Date.now() - disconnectTime });
                                // Duplexer disconnected and cannot reconnect so we try to reconnect manually
                                delete channels[channelName];
                                channelHandlerCounts[channelName]--;
                                handlerAdded = false;
                                if (connectionStatuses[getConnectionName(channelName)] !== 'connecting') {
                                    connectToDuplexer();
                                }
                                else {
                                    addConnectionEventHandlers();
                                }
                            };
                            clearInterval(reconnectTimer);
                            reconnectTimer = setInterval(function () {
                                if (navigator.onLine) {
                                    clearInterval(reconnectTimer);
                                    reconnect_1();
                                }
                            }, 1000);
                        }
                    });
                };
                var addSubscriptionHandlers = function () {
                    var subscriptionSuccessHandler = function (_a) {
                        var isSynced = _a.isSynced;
                        channels[channelName].status = 'connected';
                        logger(PubSubEventType.SUBSCRIBE_CHANNEL_SUCCESS, { channelName: channelName, eventName: eventName, isSynced: isSynced });
                        if (!handlerAdded) {
                            channelHandlerCounts[channelName] = channelHandlerCounts[channelName] || 0;
                            addEventHandlerToChannel();
                        }
                        else if (!isSynced) {
                            // we missed some messages while disconnected - do one poll to catch up
                            var endPolling = onError();
                            if (endPolling) {
                                endPolling();
                            }
                        }
                        if (onRecover && typeof onRecover === 'function') {
                            logger(PubSubEventType.END_FALLBACK, { channelName: channelName, eventName: eventName, context: 'subscription succeeded' });
                            onRecover();
                            onRecover = undefined;
                        }
                    };
                    channels[channelName].channel.on('@duplexer:subscription_succeeded', subscriptionSuccessHandler);
                    channels[channelName].channel.on('@duplexer:subscription_failed', function (_a) {
                        var status = _a.status, message = _a.message;
                        logger(PubSubEventType.SUBSCRIBE_CHANNEL_FAIL, { channelName: channelName, eventName: eventName, status: status, message: message });
                        connections[getConnectionName(channelName)].unsubscribe(getPrefixedChannelName(channelName));
                        delete channels[channelName];
                        if (!Object.keys(channels).length) {
                            connections[getConnectionName(channelName)].disconnect();
                            logger(PubSubEventType.DISCONNECT_FROM_DUPLEXER, { channelName: channelName, eventName: eventName });
                            connections[getConnectionName(channelName)] = undefined;
                        }
                        if (!onRecover) {
                            logger(PubSubEventType.FALLBACK_TO_POLLING, { channelName: channelName, eventName: eventName, context: 'subscription failed' });
                            onRecover = onError();
                        }
                        resolve(unsubscribe);
                    });
                };
                var connectToChannel = function () {
                    logger(PubSubEventType.SUBSCRIBE_CHANNEL, { channelName: channelName, eventName: eventName });
                    channels[channelName] = { channel: connections[getConnectionName(channelName)].subscribe(getPrefixedChannelName(channelName), { forceTokenRequest: true }), status: 'connecting' };
                    addSubscriptionHandlers();
                };
                var wrappedCallback = function (payload) {
                    if (isFeatureEnabled('LogDuplexerMessages') && eventName !== TICKET_UPDATED_EVENT && eventName !== PRESENCE_EVENT) {
                        logger(PubSubEventType.MESSAGE_RECEIVED, { channelName: channelName, eventName: eventName, payload: payload });
                    }
                    callback(payload);
                };
                var addEventHandlerToChannel = function () {
                    handlerAdded = true;
                    clearTimeout(timer);
                    channels[channelName].channel.on(isClient ? getPrefixedEventName(eventName) : eventName, wrappedCallback);
                    channelHandlerCounts[channelName]++;
                    logger(PubSubEventType.SUBSCRIBE_SUCCESS, { channelName: channelName, eventName: eventName });
                    resolve(unsubscribe);
                };
                var unsubscribeFromDuplexer = function () {
                    try {
                        if (channels[channelName]) {
                            channels[channelName].channel.off(isClient ? getPrefixedEventName(eventName) : eventName, wrappedCallback);
                            channelHandlerCounts[channelName]--;
                            if (channelHandlerCounts[channelName] < 1) {
                                connections[getConnectionName(channelName)].unsubscribe(getPrefixedChannelName(channelName));
                                delete channels[channelName];
                                if (!Object.keys(channels).length) {
                                    connections[getConnectionName(channelName)].disconnect();
                                    logger(PubSubEventType.DISCONNECT_FROM_DUPLEXER, { channelName: channelName, eventName: eventName });
                                    connections[getConnectionName(channelName)] = undefined;
                                }
                            }
                        }
                    }
                    catch (e) {
                        logger(PubSubEventType.UNSUBSCRIBE_RUNTIME_ERROR, { channelName: channelName, eventName: eventName, error: e });
                        // tslint:disable-next-line:no-console
                        console.log("Duplexer unsubscribe runtime error: ".concat(e));
                    }
                };
                try {
                    if (!connections[getConnectionName(channelName)]) {
                        connectToDuplexer();
                    }
                    else if (connectionStatuses[getConnectionName(channelName)] === 'connecting') {
                        addConnectionEventHandlers();
                    }
                    else if (!channels[channelName]) {
                        connectToChannel();
                        addConnectionEventHandlers();
                    }
                    else if (channels[channelName].status === 'connecting') {
                        addConnectionEventHandlers();
                        addSubscriptionHandlers();
                    }
                    else {
                        addEventHandlerToChannel();
                        addConnectionEventHandlers();
                        addSubscriptionHandlers();
                    }
                }
                catch (e) {
                    logger(PubSubEventType.SUBSCRIBE_RUNTIME_ERROR, { channelName: channelName, eventName: eventName, error: e });
                    logger(PubSubEventType.FALLBACK_TO_POLLING, { channelName: channelName, eventName: eventName, context: 'runtime error' });
                    // tslint:disable-next-line:no-console
                    console.log("Duplexer subscribe runtime error: ".concat(e));
                    if (!onRecover) {
                        onRecover = onError();
                    }
                    resolve(unsubscribe);
                }
            });
        },
        publish: function (channelName, eventName, payload, authPath, isClient) {
            logger(PubSubEventType.PUBLISH, { channelName: channelName, eventName: eventName });
            try {
                return new Promise(function (resolve, reject) {
                    var handlePublish = function () {
                        var maybePrefixedName = isClient ? getPrefixedEventName(eventName) : eventName;
                        if (!channels[channelName]) {
                            logger(PubSubEventType.SUBSCRIBE_CHANNEL, { channelName: channelName, eventName: eventName });
                            var channel_1 = connections[getConnectionName(channelName)].subscribe(getPrefixedChannelName(channelName), { forceTokenRequest: true });
                            channels[channelName] = { channel: channel_1, status: 'connecting' };
                            channel_1.on('@duplexer:subscription_succeeded', function () {
                                channels[channelName].status = 'connected';
                                logger(PubSubEventType.SUBSCRIBE_CHANNEL_SUCCESS, { channelName: channelName, eventName: eventName });
                                channel_1.send(maybePrefixedName, payload);
                                logger(PubSubEventType.SEND, { channelName: channelName, eventName: eventName });
                                resolve();
                            });
                            channel_1.on('@duplexer:subscription_failed', function (_a) {
                                var status = _a.status, message = _a.message;
                                logger(PubSubEventType.SUBSCRIBE_CHANNEL_FAIL, { channelName: channelName, eventName: eventName });
                                reject("Subscription failed with status code ".concat(status, ": ").concat(message));
                            });
                        }
                        else if (channels[channelName].status === 'connected') {
                            channels[channelName].channel.send(maybePrefixedName, payload);
                            logger(PubSubEventType.SEND, { channelName: channelName, eventName: eventName });
                            resolve();
                        }
                    };
                    if (!connections[getConnectionName(channelName)]) {
                        logger(PubSubEventType.CONNECT_TO_DUPLEXER, { channelName: channelName, eventName: eventName });
                        connections[getConnectionName(channelName)] = duplexer.connect({
                            apiKey: apiKey,
                            auth: { url: "".concat(hostUrl, "/").concat(authPath) }
                        });
                        connectionStatuses[getConnectionName(channelName)] = 'connecting';
                        connections[getConnectionName(channelName)].on('@duplexer:connected', function () {
                            connectionStatuses[getConnectionName(channelName)] = 'connected';
                            logger(PubSubEventType.DUPLEXER_CONNECTED, { channelName: channelName, eventName: eventName });
                            handlePublish();
                        });
                    }
                    else if (connectionStatuses[getConnectionName(channelName)] === 'connecting') {
                        resolve();
                    }
                    else {
                        handlePublish();
                    }
                });
            }
            catch (e) {
                logger(PubSubEventType.PUBLISH_RUNTIME_ERROR, { channelName: channelName, eventName: eventName, error: e });
                // tslint:disable-next-line:no-console
                console.log("Duplexer publish runtime error: ".concat(e));
                return Promise.resolve();
            }
        }
    };
};
//# sourceMappingURL=index.js.map